import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Languages } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  googleSignInUrl,
  useAcceptInvitation,
  useForgotPassword,
  useInvitationPreview,
  useLogin,
  useRegister,
  useResetPassword,
  useVerifyEmail,
} from "@/api/hooks";
import { ApiError } from "@/api/problem";
import type { OrganizationRole } from "@/api/types";
import { useSession } from "@/auth/session";
import { Logo } from "@/components/logo";
import { TankColumn } from "@/components/tank-column";
import { Button, Field, Input } from "@/components/ui/primitives";
import { FormError } from "@/components/ui/states";
import type { TKey } from "@/i18n/dict";
import { useI18n } from "@/i18n/provider";

const ROLE_KEY: Record<OrganizationRole, TKey> = {
  owner: "role.owner",
  admin: "role.admin",
  operator: "role.operator",
  viewer: "role.viewer",
};

/**
 * The public half of the app.
 *
 * You are INSIDE THE TANK. Deep plum, and the only thing that glows is the fuel.
 *
 * The right-hand panel is not decoration and it is not a hero image. It is a tank, at a
 * genuinely low level, with a real site name and a real litre count — the exact thing this
 * product exists to tell you about. The first sentence Tankly ever says to anyone is the
 * one it was built to say. On a narrow screen the tank steps aside entirely and the form
 * takes the page; an operations manager signing in from a phone in a generator yard needs
 * the keyboard, not the poetry.
 */
function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t, lang, setLang } = useI18n();

  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      {/* ── the form ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col px-6 py-8 sm:px-10 lg:px-16">
        <header className="flex items-center justify-between">
          <Logo className="h-7 text-cream" />

          {/* The language switch is not a setting buried in a menu. Half this product's
              users will reach for it before they reach for the password field. */}
          <button
            type="button"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            aria-label={t("lang.switchTo")}
            className="inline-flex items-center gap-1.5 rounded-[6px] border border-rule px-2.5 py-1 font-mono text-[11px] text-dim transition-colors hover:border-mauve hover:text-text"
          >
            <Languages className="size-3.5" aria-hidden />
            {t("lang.toggleShort")}
          </button>
        </header>

        <div className="flex flex-1 items-center">
          <div className="w-full max-w-[380px] py-12">
            <h1 className="text-[27px] leading-tight font-semibold text-text weight-display">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-2 text-[15px] leading-relaxed text-muted">{subtitle}</p>
            ) : null}

            <div className="mt-8">{children}</div>

            {footer ? <p className="mt-8 text-sm text-muted">{footer}</p> : null}
          </div>
        </div>

        <footer className="font-mono text-[11px] text-faint">{t("auth.legalLine")}</footer>
      </div>

      {/* ── the tank ─────────────────────────────────────────────────────── */}
      <aside className="relative hidden overflow-hidden border-s border-rule bg-well lg:block">
        {/* The tank stands on the floor of the panel and runs off the top of it — you are
            looking at part of something much larger, which is the correct impression: this
            is one tank, at one site, out of a fleet.

            It is placed on the OUTER edge (logical `end` resolves to the page edge in both
            directions) and the readout sits inboard of it, so the eye lands on the liquid
            first and the number second — the same order in which an operations manager
            actually reads a gauge. */}
        {/* The glass runs off the top of the panel: you are looking at part of something
            larger, which is the truth — this is one tank, at one site, out of a fleet.
            It stands on the floor of the panel, because a tank stands on the ground. */}
        <div className="absolute -top-20 bottom-0 end-[9%] w-[150px] xl:w-[176px]">
          <TankColumn percent={14} lowThreshold={20} />
        </div>

        {/* The readout sits inboard of the glass and is aligned to it, so the eye travels
            liquid → number. That is the order an operations manager actually reads a
            gauge in: you see how much is left before you learn the figure. */}
        <div className="relative flex h-full items-center p-12 xl:p-16">
          <div className="max-w-[280px]">
            <p className="font-mono text-[11px] text-faint label-caps">{t("auth.tankSite")}</p>
            <p className="mt-1.5 text-lg text-text weight-display">{t("auth.tankSiteName")}</p>

            <p className="mt-7 num text-[56px] leading-none text-dry">14%</p>
            <p className="mt-3 num text-sm text-dim">1,400 / 10,000 L</p>

            <p className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-dry/40 bg-dry/10 px-2.5 py-1 text-[12px] text-dry">
              <span className="size-1.5 rounded-full bg-dry" />
              {t("auth.tankLow")}
            </p>

            <p className="mt-12 max-w-[260px] text-[15px] leading-relaxed text-dim">
              {t("auth.tankLine")}
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function GoogleButton() {
  const { t } = useI18n();
  return (
    <Button
      variant="secondary"
      className="w-full"
      onClick={() => {
        // OAuth is a browser redirect, not an XHR — the spec models it as a 302 to Google.
        window.location.href = googleSignInUrl(window.location.origin);
      }}
    >
      <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"
        />
        <path
          fill="#EA4335"
          d="M12 4.75c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z"
        />
      </svg>
      {t("auth.google")}
    </Button>
  );
}

function Divider() {
  const { t } = useI18n();
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-rule" />
      <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
        {t("auth.or")}
      </span>
      <span className="h-px flex-1 bg-rule" />
    </div>
  );
}

/** Field-level failures from the RFC 7807 body, hung on the inputs they belong to. */
function useFieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fieldErrors : {};
}

/* ---------------------------------------------------------------- sign in */

export function SignInPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const fieldErrors = useFieldErrors(login.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          void navigate({ to: search.redirect ?? "/" });
        },
      },
    );
  };

  return (
    <AuthLayout
      title={t("auth.signIn")}
      subtitle={t("auth.signInSubtitle")}
      footer={
        <>
          {t("auth.noAccount")}{" "}
          <Link to="/sign-up" className="font-medium text-cream underline underline-offset-4">
            {t("auth.signUp")}
          </Link>
        </>
      }
    >
      <GoogleButton />
      <Divider />

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormError error={login.error instanceof ApiError && login.error.isFormLevel ? login.error : null} />

        <Field label={t("auth.email")} error={fieldErrors["email"]} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              dir="ltr"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field label={t("auth.password")} error={fieldErrors["password"]} required>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="current-password"
              dir="ltr"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-xs text-muted underline underline-offset-4 hover:text-text"
          >
            {t("auth.forgot")}
          </Link>
        </div>

        <Button type="submit" disabled={login.isPending} className="w-full">
          {login.isPending ? t("common.saving") : t("auth.signIn")}
        </Button>
      </form>

      {import.meta.env.VITE_USE_MOCKS === "true" ? (
        <p className="mt-4 rounded-[4px] bg-paper-sunk p-2 text-center font-mono text-[11px] text-faint">
          dana@demo-org.iq · correct-horse-battery-staple
        </p>
      ) : null}
    </AuthLayout>
  );
}

/* ---------------------------------------------------------------- sign up */

export function SignUpPage() {
  const { t } = useI18n();
  const register = useRegister();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const fieldErrors = useFieldErrors(register.error);

  if (register.isSuccess) {
    return (
      <AuthLayout title={t("auth.checkEmail")}>
        <p className="text-sm text-muted">{t("auth.checkEmailBody", { email })}</p>
        <Button asChild variant="secondary" className="mt-5 w-full">
          <Link to="/sign-in">{t("auth.signIn")}</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.signUp")}
      subtitle={t("auth.signUpSubtitle")}
      footer={
        <>
          {t("auth.hasAccount")}{" "}
          <Link to="/sign-in" className="font-medium text-cream underline underline-offset-4">
            {t("auth.signIn")}
          </Link>
        </>
      }
    >
      <GoogleButton />
      <Divider />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          register.mutate({ name, email, password });
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError
          error={register.error instanceof ApiError && register.error.isFormLevel ? register.error : null}
        />

        <Field label={t("auth.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input
              {...props}
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field label={t("auth.email")} error={fieldErrors["email"]} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              dir="ltr"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field
          label={t("auth.password")}
          error={fieldErrors["password"]}
          hint={t("auth.passwordHint")}
          required
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              dir="ltr"
              minLength={12}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Button type="submit" disabled={register.isPending} className="w-full">
          {register.isPending ? t("common.saving") : t("auth.signUp")}
        </Button>
      </form>
    </AuthLayout>
  );
}

/* ------------------------------------------------------- forgot / reset */

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const forgot = useForgotPassword();
  const [email, setEmail] = useState("");

  return (
    <AuthLayout
      title={t("auth.forgotTitle")}
      subtitle={t("auth.forgotSubtitle")}
      footer={
        <Link to="/sign-in" className="font-medium text-cream underline underline-offset-4">
          {t("auth.signIn")}
        </Link>
      }
    >
      {forgot.isSuccess ? (
        // Deliberately the same message whether or not the address exists — anything else
        // turns this form into a way to discover who has an account.
        <p role="status" className="text-sm text-muted">
          {t("auth.forgotSent")}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            forgot.mutate({ email });
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormError error={forgot.error} />

          <Field label={t("auth.email")} required>
            {(props) => (
              <Input
                {...props}
                type="email"
                autoComplete="email"
                dir="ltr"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>

          <Button type="submit" disabled={forgot.isPending} className="w-full">
            {forgot.isPending ? t("common.saving") : t("auth.forgotSend")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useI18n();
  const search = useSearch({ strict: false }) as { token?: string };
  const reset = useResetPassword();
  const [password, setPassword] = useState("");
  const fieldErrors = useFieldErrors(reset.error);

  return (
    <AuthLayout title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      {reset.isSuccess ? (
        <div className="flex flex-col gap-4">
          <p role="status" className="text-sm text-ok">
            {t("auth.resetDone")}
          </p>
          <Button asChild className="w-full">
            <Link to="/sign-in">{t("auth.signIn")}</Link>
          </Button>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            reset.mutate({ token: search.token ?? "", password });
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormError
            error={reset.error instanceof ApiError && reset.error.isFormLevel ? reset.error : null}
          />

          <Field
            label={t("auth.password")}
            error={fieldErrors["password"]}
            hint={t("auth.passwordHint")}
            required
          >
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="new-password"
                dir="ltr"
                minLength={12}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          <Button type="submit" disabled={reset.isPending} className="w-full">
            {reset.isPending ? t("common.saving") : t("auth.resetSubmit")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

/* ------------------------------------------------------------ verify email */

export function VerifyEmailPage() {
  const { t } = useI18n();
  const search = useSearch({ strict: false }) as { token?: string };
  const verify = useVerifyEmail();
  const token = search.token;

  // Fires once. The token is single-use — verifying it twice is a 410, and re-running this
  // on every render would burn the user's link before they ever saw the success state.
  useEffect(() => {
    if (token) verify.mutate({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthLayout title={t("auth.verifyTitle")}>
      {verify.isPending ? (
        <p className="text-sm text-muted">{t("auth.verifying")}</p>
      ) : verify.isSuccess ? (
        <div className="flex flex-col gap-4">
          <p role="status" className="text-sm text-ok">
            {t("auth.verifyDone")}
          </p>
          <Button asChild className="w-full">
            <Link to="/sign-in">{t("auth.signIn")}</Link>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p role="alert" className="text-sm text-dry">
            {t("auth.verifyFailed")}
          </p>
          <Button asChild variant="secondary" className="w-full">
            <Link to="/sign-in">{t("auth.signIn")}</Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}

/* ----------------------------------------------------- accept invitation */

export function AcceptInvitationPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { token = "" } = useSearch({ strict: false }) as { token?: string };
  const { isAuthenticated } = useSession();

  const preview = useInvitationPreview(token);
  const accept = useAcceptInvitation();

  return (
    <AuthLayout title={t("invite.title")}>
      {preview.isPending ? (
        <p className="text-sm text-muted">{t("common.loading")}</p>
      ) : preview.isError || !preview.data ? (
        <div className="flex flex-col gap-4">
          <p role="alert" className="text-sm text-dry">
            {t("invite.invalid")}
          </p>
          <Button asChild variant="secondary" className="w-full">
            <Link to="/sign-in">{t("auth.signIn")}</Link>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text">
            {preview.data.invited_by_name
              ? t("invite.body", {
                  inviter: preview.data.invited_by_name,
                  org: preview.data.organization_name,
                  role: t(ROLE_KEY[preview.data.role]),
                })
              : t("invite.bodyNoInviter", {
                  org: preview.data.organization_name,
                  role: t(ROLE_KEY[preview.data.role]),
                })}
          </p>

          {/* The address is masked by the server: the token must not leak a full email
              to whoever happens to open the link. */}
          <p className="rounded-[4px] bg-paper-sunk px-3 py-2 font-mono text-xs text-faint" dir="ltr">
            {preview.data.email}
          </p>

          <FormError error={accept.error} />

          {isAuthenticated ? (
            <Button
              className="w-full"
              disabled={accept.isPending}
              onClick={() =>
                accept.mutate(token, { onSuccess: () => void navigate({ to: "/" }) })
              }
            >
              {accept.isPending ? t("invite.accepting") : t("invite.accept")}
            </Button>
          ) : (
            // You cannot join an organization as nobody. Sign in first, then come back here.
            <Button asChild className="w-full">
              <Link to="/sign-in" search={{ redirect: `/invitations?token=${token}` }}>
                {t("invite.signInFirst")}
              </Link>
            </Button>
          )}
        </div>
      )}
    </AuthLayout>
  );
}
